import Script from 'next/script';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useContext } from 'use-context-selector';
import { CartContext } from '../cartContext/CartContext';

declare global {
  interface Window {
    Increasingly: {
      new (options: {
        productIds: string[];
        source: string;
        hasEnabled?: boolean;
      }): {
        initialize: () => void;
        destroy: () => void;
        productIds?: string[];
        source?: string;
        hasEnabled?: boolean;
      };
    };
    increasinglyInstance?: InstanceType<typeof window.Increasingly>;
  }
}

type IncreasinglyBundleSectionProps = {
  productIds: string[];
};

/**
 * A component that calls the Increasingly.co recommendations API and renders a
 * product bundles section into a div element.
 *
 */
export const IncreasinglyBundleSection = ({
  productIds,
}: IncreasinglyBundleSectionProps) => {
  const cartContext = useContext(CartContext);
  const { cartLinesAdd } = cartContext;

  const increasinglyObjectRef = useRef<{
    initialize: () => void;
    destroy: () => void;
    productIds?: string[];
    source?: string;
    hasEnabled?: boolean;
  } | null>(null);

  const [productIdsList, setProductIdsList] = useState(productIds);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  /**
   * Handle calling the Increasingly script
   * - Call the Increasingly script once the external JS has loaded and when the
   *   passed ids update.
   */
  const handleIncreasinglyLoad = useCallback(() => {
    if (!window.Increasingly) {
      console.warn('Increasingly script has not loaded');
      return;
    }

    const productIdsListHasItems = productIdsList.some(
      (productId) => !!productId
    );

    if (!productIdsListHasItems) {
      return;
    }

    const options = {
      productIds: productIdsList,
      source: 'increasingly-root',
    };

    /**
     * Using Increasingly function you can create new instance of our object and
     * pass the payload and initialize the instance.
     */
    increasinglyObjectRef.current = new window.Increasingly(options);
    window.increasinglyInstance = increasinglyObjectRef.current;
    increasinglyObjectRef.current?.initialize();
  }, [productIdsList]);

  const handleScriptLoad = () => {
    setScriptLoaded(true);
  };

  /**
   * If array of product ids has changed, update product list state.
   * - This then triggers the `useEffect` to re-run the increasingly script.
   */
  if (JSON.stringify(productIdsList) !== JSON.stringify(productIds)) {
    setProductIdsList(productIds);
  }

  /**
   * Re-trigger Increasingly script on ID changes
   */
  useEffect(() => {
    if (!scriptLoaded) {
      return;
    }

    if (window.Increasingly) {
      handleIncreasinglyLoad();
    }

    return () => {
      increasinglyObjectRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdsList, scriptLoaded]);

  type CustomBundleAddProduct = {
    /**
     * Shopify variant gid for add to cart payload
     */
    id: string;
    /**
     * Quantity to add to cart
     */
    quantity: number;
  };

  type CustomBundleAddEventType = {
    /**
     * Array of variant ids to use to add to cart.
     */
    products?: CustomBundleAddProduct[];
  };

  /**
   * Example for sending a custom event:
   */
  // const event = new CustomEvent('increasinglyBundleAdd', {
  //   detail: {
  //     products: [
  //       {
  //         id: 'gid://shopify/ProductVariant/4367169817826',
  //         quantity: 1, // (Optional, defaults to 1 quantity)
  //       },
  //     ],
  //   },
  // });
  // window.dispatchEvent(event);

  /**
   * Handle custom bundleAdded event.
   * - Receive a payload of ids and then call the cartLinesAdd cart function.
   */
  const handleBundleAddEvent = (
    payload: CustomEvent<CustomBundleAddEventType>
  ) => {
    if (!payload?.detail?.products) {
      return;
    }

    const cartLineAddPayload = payload.detail.products
      .map((product) => {
        return {
          id: product.id,
          quantity: product.quantity || 1,
        };
      })
      .filter((product) => product.id && product.quantity);

    if (cartLineAddPayload.length > 0) {
      cartLinesAdd(cartLineAddPayload);
    }
  };

  useEffect(() => {
    window.addEventListener(
      'increasinglyBundleAdd',
      handleBundleAddEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        'increasinglyBundleAdd',
        handleBundleAddEvent as EventListener
      );
    };
  }, []);

  return (
    <section>
      <Script
        src="https://www.increasingly.co/Implementation/o9I0uK/js/increasingly.js"
        onReady={handleScriptLoad}
      />
      <div id="increasingly-root"></div>
    </section>
  );
};
